import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { TypedMemo } from '../../../../../common/types';
import type { ReleaseReelsPlan } from '../../../../../../redux/api/fymAPI.types';
import { useSMLRouter } from '../../../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../../../routing/sml/SMLRoutes';
import { Panel } from '../../../../components/primitives/Panel';
import { planDayDate } from '../rolloutDayCopy';
import { reelsPlanMessages } from './reelsPlanMessages';
/* eslint-disable css-modules/no-unused-class -- each part uses its own slice of the two stylesheets */
import rolloutStyles from '../rollout/Rollout.module.scss';
import styles from './ReelsPlan.module.scss';
/* eslint-enable css-modules/no-unused-class */

const bold = (chunks: React.ReactNode[]) => <b>{chunks}</b>;

interface ClosedReelsPlanLineProps {
  catalogItemUuid: string;
  /** The newest plan of the song, ended or past its last day. */
  reelsPlan: ReleaseReelsPlan;
}

/**
 * The closed line of an auto campaign (design rollout-plan f4): "Auto campaign · 7 reels · Tue 8
 * Jun to Sun 20 Jun". "See the posts" opens the Posts tab of the song. The setup shows below it.
 */
const ClosedReelsPlanLineComponent: React.FC<ClosedReelsPlanLineProps> = ({ catalogItemUuid, reelsPlan }) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const seePosts = useCallback(
    () => router.push(SMLRoutes.promoteRelease, { catalogItemUuid, tab: 'posts' }),
    [catalogItemUuid, router]
  );
  return (
    <Panel as="section" data-testid="reels-plan-closed">
      <div className={styles.closed}>
        <FontAwesomeIcon icon={faCheck} />
        <span>
          {intl.formatMessage(reelsPlanMessages.closedLine, {
            reels: reelsPlan.reelCount,
            from: planDayDate(intl, reelsPlan.startDay),
            to: planDayDate(intl, reelsPlan.lastDay),
            b: bold,
          })}
        </span>
        <span className={styles.grow} />
        <button type="button" className={rolloutStyles.link} onClick={seePosts}>
          {intl.formatMessage(reelsPlanMessages.seePosts)}
        </button>
      </div>
    </Panel>
  );
};

export const ClosedReelsPlanLine = TypedMemo(ClosedReelsPlanLineComponent);
